/**
 * WarehousesTab — CRUD de bodegas con modal.
 */
import { useState } from 'react'
import { Plus, Building2, Pencil, Star, MapPin } from 'lucide-react'
import { useWarehouses, useCreateWarehouse, useUpdateWarehouse, type Warehouse } from '../hooks/useInventory'
import { usePermission } from '../../../hooks/usePermission'
import { ToastContainer, useToast } from '../../../components/Toast'
import { extractFieldErrors, extractErrorMessage } from '../../../lib/errorUtils'
import WarehouseModal from './WarehouseModal'
import LocationsModal from './LocationsModal'
import { EmptyState } from '@/components/EmptyState'

export default function WarehousesTab() {
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Partial<Warehouse> | null>(null)
  const [locationsWarehouse, setLocationsWarehouse] = useState<{ id: string; name: string } | null>(null)
  const { can } = usePermission()
  const toast = useToast()

  const { data, isLoading } = useWarehouses({ active: 'all', page, page_size: 20 })
  const { mutate: create, isPending: isCreating } = useCreateWarehouse()
  const { mutate: update, isPending: isUpdating } = useUpdateWarehouse(editing?.id || '')

  const warehouses = data?.warehouses || []

  const handleSubmit = (formData: Partial<Warehouse>) => {
    const mutation = editing?.id ? update : create
    mutation(formData, {
      onSuccess: () => {
        toast.success(editing?.id ? 'Bodega actualizada' : 'Bodega creada')
        setModalOpen(false)
        setEditing(null)
      },
      onError: (e: any) => {
        toast.error(extractErrorMessage(e, 'Error al guardar bodega'))
      },
    })
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-content-muted">{data?.count || 0} bodegas</span>
        {can('inventory.warehouses', 'create') && (
          <button
            onClick={() => { setEditing(null); setModalOpen(true) }}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover"
          >
            <Plus size={14} /> Nueva bodega
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <p className="col-span-full text-center text-sm text-content-subtle py-8">Cargando...</p>
        ) : warehouses.length === 0 ? (
          <EmptyState title="No hay bodegas registradas." size="sm" />
        ) : (
          warehouses.map(w => (
            <div
              key={w.id}
              className={`relative p-5 rounded-xl border ${w.is_active ? 'border-divider bg-surface' : 'border-divider bg-surface-muted opacity-60'} hover:shadow-md transition-shadow`}
            >
              {w.is_default && (
                <span className="absolute top-3 right-3">
                  <Star size={16} className="text-amber-400 fill-amber-400" />
                </span>
              )}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <Building2 size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-content">{w.name}</h3>
                  <p className="text-xs text-content-subtle font-mono">{w.code}</p>
                </div>
              </div>
              {w.address && <p className="text-xs text-content-muted mb-2">{w.address}</p>}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-divider">
                <span className="text-xs text-content-muted">
                  {w.stock_items_count || 0} productos con stock
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setLocationsWarehouse({ id: w.id, name: w.name })}
                    title="Ubicaciones"
                    className="p-1.5 rounded-lg hover:bg-primary-50 text-content-subtle hover:text-primary"
                  >
                    <MapPin size={14} />
                  </button>
                  {can('inventory.warehouses', 'edit') && (
                    <button
                      onClick={() => { setEditing(w); setModalOpen(true) }}
                      title="Editar"
                      className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-content-muted"
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <WarehouseModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null) }}
        onSubmit={handleSubmit}
        isPending={isCreating || isUpdating}
        initial={editing}
      />

      {locationsWarehouse && (
        <LocationsModal
          warehouseId={locationsWarehouse.id}
          warehouseName={locationsWarehouse.name}
          canEdit={can('inventory.warehouses', 'edit')}
          onClose={() => setLocationsWarehouse(null)}
        />
      )}

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}
